import { api } from '@/api';
import { Middleware, TokenResponseModel } from '@/api/generator';
import { DateTime, Duration, DurationLike } from 'luxon';
import { Auth, AuthError, Credentials } from './types';
import { createEventHook } from '@vueuse/core';
import { logConsoleErrorHandler, stopErrorPropagation } from '@/errorHandling';
import { isServerFailure } from '@/errorHandling/typeGuards';
import { set } from 'lodash';
import { HttpRequest } from 'tus-js-client';

const tokenAuthDefaultOptions: TokenAuthOptions = {
  refreshGracePeriod: { seconds: 3 },
  concurrencySafe: true,
} as const;

export interface TokenAuthOptions {
  refreshGracePeriod: DurationLike;
  concurrencySafe: boolean;
}

interface Token {
  token: string;
  expirationTime: DateTime;
}

type TokenState = 'valid' | 'expired' | 'notPresent' | 'closeToExpiration';

interface HeaderEntry {
  key: string;
  value: string;
}

export class TokenAuth implements Auth {
  private static accessToken: Token | undefined = undefined;

  private _options: TokenAuthOptions;

  constructor(options?: Partial<TokenAuthOptions>) {
    this._options = { ...tokenAuthDefaultOptions, ...(options ?? {}) };
  }

  get options(): TokenAuthOptions {
    return this._options;
  }
  set options(options: Partial<TokenAuthOptions>) {
    this._options = { ...tokenAuthDefaultOptions, ...options };
  }

  private static readonly hooks = {
    signIn: createEventHook(),
    signOut: createEventHook(),
    refreshed: createEventHook(),
    error: createEventHook<AuthError>(),
  };

  public on = {
    signIn: TokenAuth.hooks.signIn.on,
    signOut: TokenAuth.hooks.signOut.on,
    refreshed: TokenAuth.hooks.refreshed.on,
    error: TokenAuth.hooks.error.on,
  };

  get tokenState(): TokenState {
    if (TokenAuth.accessToken === undefined) return 'notPresent';

    const secondsToExpiration = TokenAuth.accessToken.expirationTime
      .diff(DateTime.now())
      .as('seconds');
    const secondsGracePeriod = Duration.fromDurationLike(this.options.refreshGracePeriod).as(
      'seconds',
    );

    if (secondsToExpiration < 0) {
      return 'expired';
    }

    if (secondsToExpiration < secondsGracePeriod) {
      return 'closeToExpiration';
    }

    return 'valid';
  }

  public get isAuthenticated() {
    return this.tokenState === 'valid' || this.tokenState === 'closeToExpiration';
  }

  public async checkAuthentication(): Promise<boolean> {
    return this.isAuthenticated || (await this.refresh());
  }

  async signIn(credentials: Credentials) {
    const { success, result } = await api.tokenAuthApi.exchangePassword(
      {
        username: credentials.emailAddress,
        password: credentials.password,
        grantType: 'password',
        clientId: `ptw-spa`,
        scope: 'ptw-api offline_access',
      },
      [stopErrorPropagation],
    );

    if (success) {
      TokenAuth.accessToken = extractToken(result);
      await TokenAuth.hooks.signIn.trigger();
    }

    return success;
  }

  async signOut() {
    const { success } = await api.tokenAuthApi.logOut([logConsoleErrorHandler]);

    if (success) {
      TokenAuth.accessToken = undefined;
      await TokenAuth.hooks.signOut.trigger();
    }

    return success;
  }

  private pendingRefreshCall: Promise<boolean> | undefined = undefined;

  private async refresh(concurrencySafe = this._options.concurrencySafe) {
    const refreshCall = async () => {
      const { success, result } = await api.tokenAuthApi.exchangeRefreshToken(
        {
          grantType: 'refresh_token',
          clientId: `ptw-spa`,
        },
        [
          async (error) => {
            // Only 400 declares an OAuth error body. Every other status is parsed as the success
            // DTO and carries no `error` field, so matching on `instanceof ResponseError` would
            // consume it here instead of leaving it to the 5xx handler below or the caller.
            if (error.name === 'OAuthErrorResponseModel') {
              const response = error.error;

              // No case returns, so every declared OAuth error is suppressed here, not just
              // invalid_grant - all of them already route through TokenAuth.hooks. An
              // unparseable body falls through to `default`.
              switch (response?.error) {
                case 'invalid_grant': {
                  // Refresh token expired
                  if (TokenAuth.accessToken) {
                    TokenAuth.accessToken = undefined;
                    await TokenAuth.hooks.signOut.trigger();
                  }
                  break;
                }
                case 'invalid_request':
                case 'unsupported_grant_type':
                default:
                  await TokenAuth.hooks.error.trigger(error);
              }
            } else {
              return error;
            }
          },
          async (error: Error) => {
            if (isServerFailure(error)) {
              await TokenAuth.hooks.error.trigger(error);
            } else {
              return error;
            }
          },
        ],
      );

      if (success) {
        const isRefreshWithoutAccessToken = this.tokenState === 'notPresent';

        TokenAuth.accessToken = extractToken(result);

        await TokenAuth.hooks.refreshed.trigger();

        if (isRefreshWithoutAccessToken) {
          await TokenAuth.hooks.signIn.trigger();
        }
      }

      return success;
    };

    this.pendingRefreshCall =
      concurrencySafe && this.pendingRefreshCall
        ? this.pendingRefreshCall
        : refreshCall().finally(() => {
            this.pendingRefreshCall = undefined;
          });

    return this.pendingRefreshCall;
  }

  private getHeader(): HeaderEntry | undefined {
    return TokenAuth.accessToken?.token
      ? {
          key: 'Authorization',
          value: `Bearer ${TokenAuth.accessToken.token}`,
        }
      : undefined;
  }

  private async commonMiddleware(addHeader: () => void) {
    switch (this.tokenState) {
      case 'valid': {
        return addHeader();
      }
      // if token is (close to) expired, try to refresh before sending request
      case 'closeToExpiration':
      case 'expired': {
        await this.refresh();
        return addHeader();
      }
      case 'notPresent':
      default:
        break;
    }
  }

  public getFetchMiddleware(): Middleware {
    return {
      pre: async (context) => {
        const addHeader = () => {
          const header = this.getHeader();

          if (header) {
            return set(context, `init.headers.${header.key}`, header.value);
          }
        };

        const isTokenAuthCall = context.url.includes('/connect/token');
        const isSignOutCall = context.url.includes('/connect/logout');

        if (isTokenAuthCall) return context;

        if (isSignOutCall) {
          return addHeader();
        }

        await this.commonMiddleware(addHeader);

        return context;
      },
    };
  }

  public getTusMiddleware() {
    return (req: HttpRequest) => {
      this.commonMiddleware(() => {
        const header = this.getHeader();

        if (header) {
          req.setHeader(header.key, header.value);
        }
      });
    };
  }
}

function extractToken(model: TokenResponseModel): Token | undefined {
  return model.accessToken && model.expiresIn
    ? {
        token: model.accessToken,
        expirationTime: DateTime.now().plus({ seconds: model.expiresIn }),
      }
    : undefined;
}
